/**
 * Who a customer step on a case or thread reaches the customer through —
 * the account's sales rep as the ask directory resolves it (the same
 * `customer_relay` the Ask modal shows for a "customer" token), fetched
 * once per scope while a customer-owed step is on screen (kanban d3915b86).
 * Null when the account has no rep on file, when data requests are dark
 * (404), or while loading; the row then says "the account's sales rep".
 */
import { useEffect, useState } from 'react';

import {
  getInvestigationAskTargets,
  getThreadAskTargets,
  type DoorAskPersonRead,
} from '../../services/qcpDataRequests';

export interface CustomerRelayScope {
  kind: 'case' | 'thread';
  id: string;
}

interface Loaded {
  /** The scope the answer belongs to; an answer for another scope is stale. */
  key: string;
  rep: DoorAskPersonRead | null;
}

export function useCustomerRelay(scope: CustomerRelayScope | null): DoorAskPersonRead | null {
  const kind = scope?.kind ?? null;
  const id = scope?.id ?? null;
  const key = kind && id ? `${kind}:${id}` : null;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  useEffect(() => {
    if (!kind || !id) return undefined;
    const mine = `${kind}:${id}`;
    let alive = true;
    (kind === 'thread' ? getThreadAskTargets(id) : getInvestigationAskTargets(id))
      .then((targets) => {
        if (alive) setLoaded({ key: mine, rep: targets.customer_relay ?? null });
      })
      .catch(() => {
        if (alive) setLoaded({ key: mine, rep: null });
      });
    return () => {
      alive = false;
    };
  }, [kind, id]);
  return loaded && loaded.key === key ? loaded.rep : null;
}
